import type {
  BankQuestionnaireMetadataResponse,
  QuestionnaireMetadata,
  QuestionnaireResponse,
  VerboseQuestionnaireMetadata
} from '@gcv/shared';
import { useQueryClient } from '@tanstack/react-query';

import { isGenericResponse } from 'api/api-util/api-response';
import { useUser } from '..';
import { BANK_QUESTIONNAIRE_KEY, BANK_QUESTIONNAIRES_METADATA_KEY } from '../keys';

/** A questionnaire's sections are stored as its custom requirements, keyed by requirement id. */
export type QuestionnaireSections = QuestionnaireResponse['custom_requirements'];

/**
 * Keeps the questionnaire list and template caches in sync after creating or updating a questionnaire
 */
export function useBankQuestionnaireCache() {
  const { user, userBankId } = useUser();
  const queryClient = useQueryClient();

  // function to convert a QuestionnaireResponse into a VerboseQuestionnaireMetadata so that we can update both object types from a questionnaire
  const getQuestionnaireMetadataFromQuestionnaireResponse = (
    questionnaire: QuestionnaireResponse
  ): VerboseQuestionnaireMetadata => ({
    template_id: questionnaire.template_id,
    latest_version: questionnaire.version,
    name: questionnaire.name,
    description: questionnaire.description,
    schedule: questionnaire.schedule,
    distribute: questionnaire.distribute,
    distributed_date: questionnaire.distributed_date,
    date_created: questionnaire.date_created,
    date_updated: questionnaire.date_updated,
    created_by: questionnaire.created_by,
    updated_by: questionnaire.updated_by,
    published_date: questionnaire.published_date,
    published_by: questionnaire.published_by,
    draft_exists: questionnaire.draft_exists,
    custom_requirement_ids: questionnaire.custom_requirements,
    is_managed_template: questionnaire.is_managed_template,
    template_category: questionnaire.template_category
  });

  const updateQuestionnaireMetadata = (
    templateId: string,
    getMetadata: (cached?: VerboseQuestionnaireMetadata) => VerboseQuestionnaireMetadata | undefined
  ) => {
    queryClient.setQueriesData<BankQuestionnaireMetadataResponse>(
      { queryKey: [BANK_QUESTIONNAIRES_METADATA_KEY, userBankId] },
      (response) => {
        const entry = response && getMetadata(response.questionnaires[templateId]);

        return response && entry
          ? { ...response, questionnaires: { ...response.questionnaires, [templateId]: entry } }
          : response;
      }
    );
  };

  /** Seeds the version the details page will ask for, so it opens without waiting on a fetch. */
  const addQuestionnaire = (questionnaire: QuestionnaireResponse) => {
    if (isGenericResponse(questionnaire)) return;

    updateQuestionnaireMetadata(questionnaire.template_id, () =>
      getQuestionnaireMetadataFromQuestionnaireResponse(questionnaire)
    );

    queryClient.setQueryData<QuestionnaireResponse>(
      [
        BANK_QUESTIONNAIRE_KEY,
        userBankId,
        questionnaire.template_id,
        questionnaire.draft_exists ? 'draft' : 'latest'
      ],
      questionnaire
    );
  };

  /** Applies the edit to the version being edited; version 1 is immutable and keeps its own copy. */
  const applyQuestionnaireUpdate = (metadata: QuestionnaireMetadata, version: string) => {
    const { template_id, name, description, schedule, distribute, distributed_date } = metadata;
    // The response carries no audit fields, so record the edit that was just saved.
    const dateUpdated = new Date().toISOString();

    updateQuestionnaireMetadata(
      template_id,
      (cached) =>
        cached && {
          ...cached,
          ...metadata,
          date_updated: dateUpdated,
          updated_by: user?.id ?? cached.updated_by
        }
    );

    queryClient.setQueryData<QuestionnaireResponse>(
      [BANK_QUESTIONNAIRE_KEY, userBankId, template_id, version],
      (questionnaire) =>
        questionnaire && {
          ...questionnaire,
          name,
          description: description ?? questionnaire.description,
          schedule,
          distribute,
          distributed_date,
          date_updated: dateUpdated,
          updated_by: user?.id ?? questionnaire.updated_by
        }
    );
  };

  /**
   * Adding, editing, archiving or reordering a section writes a draft, so the list starts reporting
   * unpublished changes and the page moves on to reading the draft.
   */
  const applyQuestionnaireSections = (
    templateId: string,
    version: string,
    sections?: QuestionnaireSections
  ) => {
    // A response carrying no template has no sections to apply.
    if (!sections) return;

    // The response carries no audit fields, so record the edit that was just saved.
    const dateUpdated = new Date().toISOString();

    updateQuestionnaireMetadata(
      templateId,
      (cached) =>
        cached && {
          ...cached,
          draft_exists: true,
          date_updated: dateUpdated,
          updated_by: user?.id ?? cached.updated_by
        }
    );

    queryClient.setQueryData<QuestionnaireResponse>(
      [BANK_QUESTIONNAIRE_KEY, userBankId, templateId, version],
      (questionnaire) =>
        questionnaire && {
          ...questionnaire,
          custom_requirements: sections,
          draft_exists: true,
          date_updated: dateUpdated,
          updated_by: user?.id ?? questionnaire.updated_by
        }
    );
  };

  /** Publishing consumes the draft, so the published version takes over and the draft is dropped. */
  const applyPublishedQuestionnaire = (questionnaire: QuestionnaireResponse) => {
    if (isGenericResponse(questionnaire)) return;

    const { template_id } = questionnaire;

    queryClient.setQueryData<QuestionnaireResponse>(
      [BANK_QUESTIONNAIRE_KEY, userBankId, template_id, 'latest'],
      questionnaire
    );
    // Publishing creates a new version, so keep the questionnaire's original date_created.
    updateQuestionnaireMetadata(template_id, (cached) => ({
      ...getQuestionnaireMetadataFromQuestionnaireResponse(questionnaire),
      date_created: cached?.date_created ?? questionnaire.date_created
    }));
    queryClient.removeQueries({
      queryKey: [BANK_QUESTIONNAIRE_KEY, userBankId, template_id, 'draft'],
      exact: true
    });
  };

  return {
    addQuestionnaire,
    applyQuestionnaireUpdate,
    applyQuestionnaireSections,
    applyPublishedQuestionnaire
  };
}
